import React from 'react';
import I18n from 'i18n-js';

import buildSiteFooterData, { SiteFooterLinkData } from './site_footer_data';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import NewsletterSignup from '@reverbdotcom/commons/src/components/newsletter_signup';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import UserPrivacyConsentContainer from './user_privacy_consent_container';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { RCAccordion, RCThemeOverride, useMediaQuery } from '@reverbdotcom/cadence/components';

interface SiteFooterLinkProps {
  linkData: SiteFooterLinkData;
}

function SiteFooterLink({ linkData }: SiteFooterLinkProps) {
  return (
    <CoreLink
      to={linkData.href}
      className="site-footer-v2__link"
      clickEvent={{
        eventName: MParticleEventName.ClickedComponent,
        componentName: 'SiteFooter',
        entityType: 'url',
        entityId: linkData.href,
      }}
    >
      {I18n.t(`discovery.siteFooter.${linkData.textKey}`)}
    </CoreLink>
  );
}

interface SiteFooterLinkSectionProps {
  titleKey: string;
}

export function SiteFooterLinkSection({ titleKey }: SiteFooterLinkSectionProps) {
  const user = useUser();
  const { locale, countryCode } = user;
  const siteFooterData = buildSiteFooterData(user);
  const section = siteFooterData.find(data => data.titleKey === titleKey);
  const isMobile = useMediaQuery('mobile');
  const filterLinksByUserAttributes = link => {
    if (countryCode !== 'US' && link.usOnly) {
      return false;
    }

    if (locale !== 'en' && link.enLocaleOnly) {
      return false;
    }

    return true;
  };
  const renderPrivacySettings = titleKey === 'help';

  function renderLinks() {
    return (
      <>
        {section.links.filter(filterLinksByUserAttributes).map(linkData => (
          <SiteFooterLink
            linkData={linkData}
            key={`footer_${linkData.textKey}`}
          />
        ))}
        {renderPrivacySettings && <UserPrivacyConsentContainer isFooterLink />}
      </>
    );
  }

  return (
    <div className="site-footer-v2__link-section">
      {isMobile ? (
        <RCAccordion
          heading={I18n.t(`discovery.siteFooter.${section.titleKey}`)}
          id={section.titleKey}
        >
          {renderLinks()}
        </RCAccordion>
      ) : (
        <>
          <h3 className="text-default-400 text-default--bold mb-2">
            {I18n.t(`discovery.siteFooter.${section.titleKey}`)}
          </h3>
          {renderLinks()}
        </>
      )}
    </div>
  );
}

export default function SiteFooter() {
  return (
    <RCThemeOverride theme="dark">
      <div className="site-footer-v2">
        <div className="site-footer-v2__inner">
          <div className="site-footer-v2__link-columns">
            <div className="site-footer-v2__link-column">
              <SiteFooterLinkSection titleKey="shopGear" />
            </div>
            <div className="site-footer-v2__link-column">
              <SiteFooterLinkSection titleKey="sell" />
              <SiteFooterLinkSection titleKey="resources" />
            </div>
            <div className="site-footer-v2__link-column">
              <SiteFooterLinkSection titleKey="help" />
            </div>
            <div className="site-footer-v2__link-column">
              <SiteFooterLinkSection titleKey="company" />
            </div>
          </div>
          <div className="site-footer-v2__newsletter">
            <RCThemeOverride theme="light">
              <div>
                <NewsletterSignup
                  title={I18n.t('discovery.siteFooter.newsletterHeader')}
                  signupReason="footer_signup"
                />
              </div>
            </RCThemeOverride>
          </div>
        </div>
      </div>
    </RCThemeOverride>
  );
}
